import React from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { ReactComponent as CloseIcon } from "../../assets/images/x.svg";
import { colorCodesForAvatar } from "../../constants/index";
import { chracterPosition } from "../../utils/index";
import PlatformStatusChip from "./PlatformStatusChip";
import { getAvailableActions, usePlatformAdmin } from "./PlatformAdminContext";
import { formatPlatformDate } from "./platformAdminApi";
import {
  InfoField,
  inter,
  primaryButtonSx,
  secondaryButtonSx,
} from "./platformUi";

function DrawerSection({ title, children }) {
  return (
    <Box sx={{ borderBottom: "1px solid #EEF2F6", py: 2 }}>
      <Typography sx={{ color: "#061522", fontFamily: inter, fontSize: 14, fontWeight: 500, lineHeight: "18px", mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function BusinessDetailDrawer({ business, onClose, onViewDetail, onActionDone }) {
  const { requestAction } = usePlatformAdmin();
  if (!business) return null;

  const letter = (business.name || "?").substring(0, 1).toUpperCase();
  const actions = getAvailableActions(business);

  return (
    <>
      <Box onClick={onClose} sx={{ position: "fixed", inset: 0, bgcolor: "rgba(6, 21, 34, 0.2)", zIndex: 1200 }} />
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          width: 360,
          height: "100vh",
          bgcolor: "#FFF",
          boxShadow: "0 4px 80px 0 rgba(6, 21, 34, 0.10)",
          zIndex: 1201,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, overflowY: "auto", px: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 2.5 }}>
            <Avatar
              variant="rounded"
              sx={{
                width: 44,
                height: 44,
                fontSize: 16,
                borderRadius: "10px",
                bgcolor: colorCodesForAvatar[chracterPosition(letter)] || "#405261",
              }}
            >
              {letter}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  sx={{ flex: 1, minWidth: 0, color: "#061522", fontFamily: inter, fontSize: 16, fontWeight: 500, lineHeight: "20px" }}
                  noWrap
                >
                  {business.name}
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{ p: 0, mr: "-4px" }}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Typography sx={{ color: "#405261", fontFamily: inter, fontSize: 13, mt: 0.25 }} noWrap>
                {business.slug || "—"}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 1.25, flexWrap: "wrap" }}>
                <PlatformStatusChip status={business.status} />
              </Box>
            </Box>
          </Box>

          <Box sx={{ borderBottom: "1px solid #E5E9EC", mx: -2.5 }} />

          <DrawerSection title="Owner">
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
              <InfoField label="Name" value={business.ownerName} />
              <InfoField label="Email" value={business.ownerEmail} />
            </Box>
          </DrawerSection>

          <DrawerSection title="Business">
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
              <InfoField label="Phone" value={business.phone} />
              <InfoField label="Website" value={business.website} />
              <InfoField label="Created" value={formatPlatformDate(business.createdAt)} />
            </Box>
          </DrawerSection>

          <DrawerSection title="Actions">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {actions.map((a) => (
                <Button
                  key={a.key}
                  onClick={() =>
                    requestAction(a.key, business, {
                      onDone: () => onActionDone?.(a.key),
                    })
                  }
                  sx={{
                    ...secondaryButtonSx,
                    justifyContent: "flex-start",
                    ...(a.danger ? { color: "#FF4D4F" } : {}),
                  }}
                >
                  {a.label}
                </Button>
              ))}
            </Box>
          </DrawerSection>
        </Box>

        <Box sx={{ px: 2.5, py: 2, display: "flex", gap: 1, borderTop: "1px solid #EEF2F6" }}>
          <Button onClick={onViewDetail} sx={{ ...primaryButtonSx, flex: 1 }}>
            View Detail
          </Button>
        </Box>
      </Box>
    </>
  );
}
